'use client';

/**
 * PlannerEmptyState — the trip planner's first screen.
 *
 * One field, one button, on every screen. The page says what it is for
 * ("Plan a road trip"), asks one question ("Where are you starting?") and
 * offers one primary action ("Start planning"). One-tap starts (capital
 * cities) sit under the field for people who'd rather not type. The end
 * point, a "Back home" loop and a leaving date are optional context behind a
 * single "Add an end point or date" link (open once a start is picked).
 * Under the form sits a short list of popular road trips to take instead;
 * pointing at one lights its line up (and flies the map to it).
 *
 * On phones (no map beside the panel) a 140px drawn map of those trips
 * leads the panel, so "real trips to steal" is above the fold there too;
 * tapping it opens the full map. It also drops "Start planning" into the
 * thumb zone.
 */

import SavedSharedTrips from './SavedSharedTrips';
import React, { useEffect, useId, useRef, useState } from 'react';
import LocationAutocomplete, { type ResolvedLocation } from '../LocationAutocomplete';
import PopularTripsPicker from './PopularTripsPicker';
import DateButton from './DateButton';
import RouteMapThumb from './RouteMapThumb';
import type { PopularRouteSummary } from '../../utils/popularTripsApi';
import { QUICK_STARTS } from './plannerStart';

export interface StartPlan {
  start: ResolvedLocation;
  end: ResolvedLocation | null;
  loop: boolean;
  /** YYYY-MM-DD, or null when not given. */
  startDate: string | null;
}

interface PlannerEmptyStateProps {
  onStart: (plan: StartPlan) => void;
  /** Focus the start field on arrival — honoured on desktop only. */
  autoFocus?: boolean;
  /** Drop-a-pin: a quiet option among the extras, desktop only (needs the map). */
  addWaypointMode: boolean;
  onToggleDropPin: () => void;
  showPopular: boolean;
  popularRoutes: PopularRouteSummary[] | null;
  popularLoadingId: string | null;
  onTakePopular: (route: PopularRouteSummary) => void;
  onRetryPopular: () => void;
  onSeeAllPopular: () => void;
  /** A popular trip is being pointed at (hover/focus) — null when none. */
  onPreviewRoute?: (routeId: string | null) => void;
  /** Phones: open the full-screen map from the map strip. */
  onOpenMap?: () => void;
  /** Phones: the drawn map strip. Off where a real map already sits above
   *  the panel (the phone planner's map + sheet layout). */
  mapStrip?: boolean;
}

function todayIsoLocal(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function LoopGlyph({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M17 2l3 3-3 3" />
      <path d="M4 11V9a4 4 0 014-4h12" />
      <path d="M7 22l-3-3 3-3" />
      <path d="M20 13v2a4 4 0 01-4 4H4" />
    </svg>
  );
}

function ArrowGlyph({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M5 12h14M13 6l6 6-6 6" />
    </svg>
  );
}

function PlusGlyph({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

function MapGlyph({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M9 4L3 6.5v13L9 17l6 3 6-2.5v-13L15 7 9 4z" />
      <path d="M9 4v13M15 7v13" />
    </svg>
  );
}

function PinDropGlyph({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 21s-6.5-6.1-6.5-11a6.5 6.5 0 0113 0c0 4.9-6.5 11-6.5 11z" />
      <circle cx="12" cy="10" r="2.3" />
    </svg>
  );
}

/** Fade + lift in on mount; nothing at all under prefers-reduced-motion. */
function useEntered(): boolean {
  const [entered, setEntered] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(id);
  }, []);
  return entered;
}

function Reveal({ children }: { children: React.ReactNode }) {
  const entered = useEntered();
  return (
    <div
      className={`transition-[opacity,transform] duration-200 ease-out motion-reduce:transition-none ${
        entered ? 'translate-y-0 opacity-100' : '-translate-y-1 opacity-0'
      }`}
    >
      {children}
    </div>
  );
}

/** Phones only: a 140px drawn map of where the popular trips go, at the top
 *  of the panel. Opens the full map. */
function MapStrip({ routes, onOpen }: { routes: PopularRouteSummary[] | null; onOpen?: () => void }) {
  const count = routes?.length ?? 0;
  const body = (
    <>
      <span className="relative z-10 flex min-w-0 flex-1 flex-col justify-between self-stretch py-3.5 pl-4 pr-1 text-left">
        <span>
          <span className="block text-[15px] font-semibold leading-5 tracking-tight text-tp-ink">
            {count > 0 ? `${count} road trips, mapped` : 'Road trips, mapped'}
          </span>
          <span className="mt-0.5 block text-[13px] leading-[18px] text-tp-muted">Camps on the way included.</span>
        </span>
        {onOpen && (
          <span className="inline-flex h-8 w-fit items-center gap-1.5 whitespace-nowrap rounded-lg bg-white px-3 text-[13px] font-semibold text-tp-brand shadow-[0_1px_2px_rgba(21,88,176,0.15)] ring-1 ring-tp-brand/20">
            <MapGlyph className="h-3.5 w-3.5" />
            Open the map
          </span>
        )}
      </span>
      <RouteMapThumb routes={routes ?? []} tone="strip" className="h-full w-[46%] max-w-[180px] flex-none py-2 pr-2" />
    </>
  );
  const cls =
    'flex h-[140px] w-full items-center overflow-hidden rounded-2xl bg-tp-brand-soft ring-1 ring-inset ring-tp-brand/10';
  return onOpen ? (
    <button
      type="button"
      onClick={onOpen}
      aria-label="Open the full map"
      className={`${cls} transition-shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tp-brand`}
    >
      {body}
    </button>
  ) : (
    <div className={cls}>{body}</div>
  );
}

export default function PlannerEmptyState({
  onStart,
  autoFocus = false,
  addWaypointMode,
  onToggleDropPin,
  showPopular,
  mapStrip = true,
  popularRoutes,
  popularLoadingId,
  onTakePopular,
  onRetryPopular,
  onSeeAllPopular,
  onPreviewRoute,
  onOpenMap,
}: PlannerEmptyStateProps) {
  const uid = useId();
  const startErrorId = `${uid}-start-error`;
  const endErrorId = `${uid}-end-error`;
  const loopId = `${uid}-loop`;
  const extrasId = `${uid}-extras`;

  const startWrapRef = useRef<HTMLDivElement | null>(null);
  const endWrapRef = useRef<HTMLDivElement | null>(null);

  const [start, setStart] = useState<ResolvedLocation | null>(null);
  const [startText, setStartText] = useState('');
  const [end, setEnd] = useState<ResolvedLocation | null>(null);
  const [endText, setEndText] = useState('');
  const [loop, setLoop] = useState(false);
  const [startDate, setStartDate] = useState<string | null>(null);
  const [startError, setStartError] = useState<string | null>(null);
  const [endError, setEndError] = useState<string | null>(null);
  // The extras stay hidden until there is a start, or someone asks for them —
  // the same on phones and desktop: one field, then the button.
  const [extrasAsked, setExtrasAsked] = useState(false);
  const extrasOpen = extrasAsked || !!start;
  // "Start planning" with nothing typed: focus the field and give it a nudge.
  const [nudge, setNudge] = useState(0);
  // Desktop only: focusing on a phone pops the keyboard over the page.
  const [focusStart, setFocusStart] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const desktop = window.matchMedia('(min-width: 768px) and (pointer: fine)').matches;
    setIsDesktop(window.matchMedia('(min-width: 768px)').matches);
    if (autoFocus) setFocusStart(desktop);
  }, [autoFocus]);

  const focusIn = (wrap: HTMLDivElement | null) =>
    wrap?.querySelector<HTMLInputElement>('input[role="combobox"]')?.focus();

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!start) {
      // Empty: no scolding, just point at the field. Typed but not picked:
      // say what's missing.
      setStartError(startText.trim() ? 'Pick your starting point from the list.' : null);
      setNudge((n) => n + 1);
      focusIn(startWrapRef.current);
      return;
    }
    // Typed an end but never picked one: don't silently drop it.
    if (!loop && !end && endText.trim()) {
      setEndError('Pick your end point from the list, or clear it.');
      focusIn(endWrapRef.current);
      return;
    }
    onStart({
      start,
      end: loop ? null : end,
      loop,
      startDate: startDate && /^\d{4}-\d{2}-\d{2}$/.test(startDate) ? startDate : null,
    });
  };

  return (
    <div className="px-5 pb-5 pt-5" data-testid="planner-empty-state">
      {/* Phones: the map leads (no map beside the panel there). */}
      {showPopular && mapStrip && (
        <div className="mb-5 md:hidden" data-testid="planner-map-strip">
          <MapStrip routes={popularRoutes} onOpen={onOpenMap} />
        </div>
      )}
      {/* No collapse control here: folding away the only task leaves nothing. */}
      <h1 className="text-[28px] font-bold leading-[34px] tracking-[-0.02em] text-tp-ink">
        Plan a road trip
      </h1>
      <p className="mt-2 text-[14px] leading-5 text-tp-muted">
        Start with one town. We’ll map the drive and the camps on the way.
      </p>


      <form onSubmit={submit} noValidate aria-label="Start a road trip" className="mt-5">
        <div
          ref={startWrapRef}
          data-testid="planner-start"
          data-nudge={nudge % 2 === 1 ? 'a' : nudge > 0 ? 'b' : undefined}
          className="tp-nudge"
        >
          <LocationAutocomplete
            size="large"
            label="Where are you starting?"
            placeholder="Town, park or campsite"
            value={start}
            onChange={(loc) => {
              setStart(loc);
              if (loc) setStartError(null);
            }}
            onTextChange={(t) => {
              setStartText(t);
              setStartError(null);
            }}
            allowMyLocation
            autoFocus={focusStart}
            invalid={!!startError}
            describedBy={startError ? startErrorId : undefined}
          />
          {startError && (
            <p id={startErrorId} role="alert" className="mt-1.5 text-[13px] font-medium text-red-700">
              {startError}
            </p>
          )}
        </div>

        {!start && (
          // Three one-tap starts: they fit a 350px phone column and the
          // 380px panel without scrolling, clipping or a fade.
          <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="quick-starts">
            <span className="flex-none text-[14px] text-tp-muted" id={`${uid}-try`}>
              Try
            </span>
            {QUICK_STARTS.slice(0, 3).map((q) => (
              <button
                key={q.label}
                type="button"
                aria-describedby={`${uid}-try`}
                onClick={() => {
                  setStart({ label: q.label, lat: q.lat, lng: q.lng, kind: 'place' });
                  setStartText(q.label);
                  setStartError(null);
                }}
                className="h-9 min-h-0 flex-none whitespace-nowrap rounded-lg bg-white px-3 text-[14px] font-medium text-tp-muted ring-1 ring-inset ring-tp-line-strong transition-colors hover:bg-tp-brand/[0.05] hover:text-tp-brand hover:ring-tp-brand/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tp-brand/40"
              >
                {q.label}
              </button>
            ))}
          </div>
        )}

        {!extrasOpen && (
          <button
            type="button"
            onClick={() => setExtrasAsked(true)}
            aria-expanded={false}
            aria-controls={extrasId}
            className="mt-3 inline-flex h-9 min-h-0 items-center gap-1.5 rounded-lg px-1 text-[14px] font-medium text-tp-muted transition-colors hover:text-tp-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tp-brand/40"
          >
            <PlusGlyph className="h-4 w-4" />
            Add an end point or date
          </button>
        )}

        {extrasOpen && (
        <Reveal>
            <div id={extrasId} className="mt-4 space-y-4">
              <div ref={endWrapRef}>
                {/* The checkbox sits on the label row so ticking it never
                    moves the field below. */}
                <div className="flex min-h-[24px] items-center justify-between gap-3">
                  <span className="text-[14px] font-semibold text-tp-ink" aria-hidden="true">
                    Finishing at <span className="font-normal text-tp-muted">optional</span>
                  </span>
                  <label htmlFor={loopId} className="inline-flex cursor-pointer select-none items-center gap-2 text-[14px] text-tp-muted">
                    <input
                      id={loopId}
                      type="checkbox"
                      checked={loop}
                      onChange={(e) => {
                        setLoop(e.target.checked);
                        setEndError(null);
                      }}
                      className="h-4 w-4 cursor-pointer rounded border-tp-line-strong accent-tp-brand"
                    />
                    Back home (loop)
                  </label>
                </div>
                <div className="mt-1.5">
                  {loop ? (
                    <div
                      className="flex h-11 items-center gap-2 rounded-lg border border-dashed border-tp-line-strong bg-tp-surface px-3 text-[15px] text-tp-muted"
                      aria-live="polite"
                    >
                      <LoopGlyph className="h-4 w-4 flex-none text-tp-brand" />
                      <span className="truncate">
                        {start ? `Back to ${start.label}` : 'Back to where you start'}
                      </span>
                    </div>
                  ) : (
                    <>
                      <LocationAutocomplete
                        size="medium"
                        ariaLabel="Finishing at (optional)"
                        placeholder="Decide later"
                        value={end}
                        onChange={(loc) => {
                          setEnd(loc);
                          if (loc) setEndError(null);
                        }}
                        onTextChange={(t) => {
                          setEndText(t);
                          setEndError(null);
                        }}
                        allowMyLocation={false}
                        invalid={!!endError}
                        describedBy={endError ? endErrorId : undefined}
                      />
                      {endError && (
                        <p id={endErrorId} role="alert" className="mt-1.5 text-[13px] font-medium text-red-700">
                          {endError}
                        </p>
                      )}
                    </>
                  )}
                </div>
              </div>

              <div>
                <span className="block text-[14px] font-semibold text-tp-ink" aria-hidden="true">
                  Leaving on <span className="font-normal text-tp-muted">optional</span>
                </span>
                <DateButton
                  className="mt-1.5 w-full"
                  label="Leaving on"
                  value={startDate}
                  onChange={setStartDate}
                  min={todayIsoLocal()}
                />
              </div>

              {isDesktop && (
                <p className="text-[13px] text-tp-muted">
                  {addWaypointMode ? (
                    <>
                      Click the map to drop your first stop.{' '}
                      <button type="button" onClick={onToggleDropPin} className="font-semibold text-tp-ink underline underline-offset-2 hover:text-tp-ink">
                        Cancel
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      onClick={onToggleDropPin}
                      className="inline-flex items-center gap-1.5 font-medium text-tp-muted transition-colors hover:text-tp-brand"
                    >
                      <PinDropGlyph className="h-4 w-4" />
                      Or drop a pin on the map
                    </button>
                  )}
                </p>
              )}
            </div>
        </Reveal>
        )}

        {/* 16px under the last control: field, chips and button read as one
            unit. Until there's a start the button sits at reduced emphasis
            (a brand tint, not grey: still clearly the way forward); a tap
            then focuses the field and nudges it. It fills brand blue the
            moment a town is picked. */}
        <button
          type="submit"
          data-variant="primary"
          data-ready={start ? 'true' : 'false'}
          aria-describedby={start ? undefined : `${uid}-start-hint`}
          className={`ca-start-planning mt-4 inline-flex h-12 w-full items-center justify-center gap-2 rounded-lg text-[16px] font-semibold transition-[background-color,color,box-shadow,transform] duration-200 active:scale-[0.99] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-tp-brand/30 motion-reduce:transition-none ${
            start
              ? 'bg-tp-brand text-white shadow-[0_1px_2px_rgba(21,88,176,0.25),0_4px_12px_-4px_rgba(21,88,176,0.45)] hover:bg-tp-brand-ink'
              : 'bg-tp-brand/[0.1] text-tp-brand hover:bg-tp-brand/[0.16]'
          }`}
        >
          Start planning
          <ArrowGlyph className="h-4 w-4" />
        </button>
        {!start && (
          <span id={`${uid}-start-hint`} className="sr-only">
            Pick a starting town first.
          </span>
        )}
      </form>

      <SavedSharedTrips className="mt-5 border-t border-tp-line/80 pt-5" />

      {showPopular && (
        <div className="mt-5 border-t border-tp-line/80 pt-5">
          <PopularTripsPicker
            variant="shelf"
            routes={popularRoutes}
            loadingId={popularLoadingId}
            onTake={onTakePopular}
            onRetry={onRetryPopular}
            onSeeAll={onSeeAllPopular}
            onPreview={onPreviewRoute}
            currentStopCount={0}
          />
        </div>
      )}
    </div>
  );
}
